/**
 * Routify Pro — pipeline orchestrator.
 *
 * Deterministic-first design: intent → candidates (Places) → clustering →
 * TSP → schedule + meals → AI narrator. Isolated from the Premium generator.
 */
import { cachedPlacesFetch, PLACES_TTL } from "../places-http-cache.server";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { kmeans, type Poi } from "./clustering";
import { orderStops } from "./optimizer";
import { schedule, fmtTime, type PlannedStop } from "./meals";
import { travelMinutes, modeLabel, type Mode } from "./transport";
import { haversineMeters } from "./geo";
import { planCopy } from "@/lib/plan-copy";
import {
  attachGenerationQuotaInternal,
  beginGenerationAttemptInternal,
  finishGenerationAttemptInternal,
} from "@/lib/quota.server";
import { generationGuardMessage, isUsableProPlan } from "./generation-policy";
import {
  parseNegativePreferences,
  isCategoryExcluded,
  guessCategoryFromText,
  type NegativePreferences,
} from "@/lib/negative-preferences";
import type { MealPreference } from "@/lib/meal-preference";
import {
  buildTripPreferences,
  interestsFromPreferences,
  toNegativePreferences,
} from "@/lib/trip-preferences";
import { detectPrimaryObjective } from "@/lib/primary-objective";
import {
  checkCorridorCompliance,
  corridorForTemplate,
  findCorridorInText,
} from "@/lib/catalog/corridor-routes";
import { resolveCityAnchors } from "./city-anchors";
import { isValidMultiDestinationPlan, parseMultiDestinationPlan, sanitizeDestinationName, normalizeDestinationKey, contractAnchorMismatches, type MultiDestinationPlan } from "./multi-destination";
import { applyInterDestinationTransfers } from "./inter-destination-transfers";
import { enforceInterestBalance } from "./interest-compliance";
import { buildRouteSkeleton } from "./route-skeleton";
import { preflightProGeneration } from "./generation-preflight";
import {
  isMultimodalPlan,
  planMultimodalDay,
  type MultimodalPlan,
} from "./multimodal";
import { parseTransportPlan } from "@/lib/request-contract";
import type { TransportMode } from "@/lib/transport-modes";

const INTEREST_TO_EXCLUDED: Partial<Record<string, "restaurant" | "shopping" | "nightlife">> = {
  food: "restaurant",
  shopping: "shopping",
  nightlife: "nightlife",
};

/** Passe final: remove qualquer paragem cuja categoria tenha sido excluída. */
function dropExcludedStops(days: ProDay[], prefs: NegativePreferences): ProDay[] {
  if (prefs.excludedCategories.length === 0) return days;
  return days.map((day) => ({
    ...day,
    stops: day.stops.filter((stop) => {
      if (
        stop.kind === "meal" ||
        stop.kind === "break" ||
        stop.kind === "transfer" ||
        stop.kind === "parking"
      )
        return true;
      const guess = guessCategoryFromText(stop.name) ?? guessCategoryFromText(stop.category);
      return !guess || !isCategoryExcluded(prefs, guess);
    }),
  }));
}

const GenerateInput = z.object({
  prompt: z.string().min(3).max(4000),
  cities: z.array(z.string().max(120)).max(20).default([]),
  days: z.number().int().min(1).max(30).default(3),
  transport: z.enum(["walk", "bike", "car", "transit"]).default("walk"),
  /** Modos locais adicionais (aditivo: vazio = comportamento de sempre). */
  secondaryModes: z
    .array(z.enum(["walk", "bike", "scooter", "motorcycle", "car", "transit"]))
    .max(6)
    .default([]),
  /** Sugestões de aluguer: opcionais e nunca bloqueiam a geração. */
  rentalSuggestions: z.boolean().default(false),
  pace: z.enum(["slow", "normal", "intense"]).default("normal"),
  interests: z.array(z.string().max(60)).max(30).default([]),
  accommodation: z.string().max(240).optional(),
  originCoords: z.object({ lat: z.number(), lng: z.number() }).optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  requestId: z
    .string()
    .regex(/^[A-Za-z0-9_-]{16,80}$/)
    .optional(),
  templateId: z.string().max(120).optional(),
  templateMode: z.enum(["full", "partial"]).optional(),
  routeReturn: z.enum(["excluded", "included"]).optional(),
  /** Opção de reparação escolhida pelo utilizador (nova tentativa sem crédito). */
  repairStrategy: z
    .enum(["fewer_cities_full_experience", "more_nature_partial_experience"])
    .optional(),
  /** Roteiro draft que legitima uma reparação sem nova reserva de quota. */
  repairOfId: z.string().uuid().optional(),
  accommodationZoneCount: z.number().int().min(0).max(4).default(0),
  /** Preferência de refeições normalizada e vinculativa (UI > texto > default). */
  mealPreference: z.enum(["restaurant", "free_break", "none"]).optional(),
  multiDestinationPlan: z
    .object({
      tripType: z.literal("multi_destination"),
      stops: z
        .array(
          z.object({
            destination: z.string().min(2).max(40),
            key: z.string().min(2).max(80),
            days: z.number().int().min(1).max(60),
            order: z.number().int().min(1).max(20),
          }),
        )
        .min(2)
        .max(20),
      totalDays: z.number().int().min(2).max(60),
    })
    .optional(),
  /** Locais nomeados exigidos no pedido ("que inclua X, Y, Z"). Nunca são cidades. */
  requiredStops: z.array(z.string().min(2).max(120)).max(12).default([]),
});

export type ProDayStop = {
  kind: "poi" | "meal" | "break" | "parking" | "transfer" | "experience";
  category?: "lake" | "activity" | "scenic_train" | "transfer" | string;
  name: string;
  time: string;
  durationMin: number;
  lat?: number;
  lng?: number;
  placeId?: string;
  photoUrl?: string;
  travelFromPrev?: { minutes: number; meters: number; label: string; mode?: TransportMode };
  note?: string;
  officialUrl?: string;
  templateId?: string;
  /** Restaurante real associado a uma pausa de refeição (nunca inventado). */
  venue?: {
    name: string;
    lat: number;
    lng: number;
    placeId?: string;
    address?: string;
    rating?: number | null;
  };
};

export type ProDay = { day: number; city: string; intro?: string; stops: ProDayStop[] };
export type ProSuggestion = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address?: string;
  rating?: number | null;
  note?: string;
};
export type ProSuggestions = {
  brief?: string;
  /** Locais reais de aluguer (só quando pedidos). Sem preços nem horários. */
  rentals?: Array<ProSuggestion & { mode: TransportMode; pickupZone?: string }>;
  hotel?: ProSuggestion & { preferences?: string };
  restaurants?: ProSuggestion[];
};
export type ProPlan = {
  id?: string;
  title: string;
  status: "draft" | "ready";
  transport: Mode;
  /** Plano multimodal (opcional): principal + modos locais. */
  transportPlan?: { primary: TransportMode; secondary: TransportMode[]; hybrid: boolean };
  /** Todos os modos disponíveis no roteiro (compatível com roteiros antigos). */
  allowedModes?: TransportMode[];
  /** Modo real de condução mostrado ao viajante (ex.: mota numa estrada mítica). */
  rideMode?: "motorcycle";
  pace: "slow" | "normal" | "intense";
  mood?: string;
  days: ProDay[];
  suggestions?: ProSuggestions;
  compliance?: {
    valid: boolean;
    repaired: boolean;
    violations: Array<{ code: string; message: string; kind?: string; missingCount?: number }>;
    repairCandidates: ProSuggestion[];
    repairQuestion?: {
      code: string;
      message: string;
      options: Array<{ id: string; label: string; description: string }>;
      hint: string;
    };
    /** Opções clicáveis que reconstroem o esqueleto sem consumir novo crédito. */
    repairStrategies?: {
      message: string;
      hint: string;
      options: Array<{
        id: string;
        label: string;
        description: string;
        patch: {
          cities: string[];
          days: number;
          transport: string;
          templateId: string;
          templateName: string;
          templateMode: "full" | "partial";
          accommodationZoneMin: number;
          themes: string[];
        };
      }>;
    };
    /** Preenchido quando não existe experiência curada segura: perguntamos. */
    repairAsk?: { message: string; hint: string };
  };
};

async function hasProAccess(userId: string): Promise<boolean> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { resolveEffectiveTierInternal } = await import("@/lib/quota.server");
  const entitlement = await resolveEffectiveTierInternal(supabaseAdmin, userId);
  return entitlement.tier === "pro";
}

async function ensurePro(_supabase: unknown, userId: string) {
  if (!(await hasProAccess(userId))) throw new Error("Pro plan required");
}

type ProUsageReservation = { usageCounterId: number };

async function enforceProRateLimit(userId: string): Promise<ProUsageReservation> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { getEffectiveLimits } = await import("@/lib/quota.server");
  const limits = await getEffectiveLimits(supabaseAdmin);
  const since30 = new Date(Date.now() - 24 * 30 * 60 * 60 * 1000).toISOString();
  const since1 = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const [monthly, daily] = await Promise.all([
    supabaseAdmin
      .from("usage_counters")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("kind", "pro_itinerary")
      .gte("created_at", since30),
    supabaseAdmin
      .from("usage_counters")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("kind", "pro_itinerary")
      .gte("created_at", since1),
  ]);
  if (monthly.error || daily.error)
    throw new Error("Não foi possível validar o limite Pro. Tenta novamente.");
  if ((monthly.count ?? 0) >= limits.pro.monthly) {
    throw new Error(planCopy("pt").proMonthlyReached(limits));
  }
  if ((daily.count ?? 0) >= limits.pro.daily) {
    throw new Error(planCopy("pt").proDailyReached(limits));
  }
  const { data, error } = await supabaseAdmin
    .from("usage_counters")
    .insert({ user_id: userId, kind: "pro_itinerary", source: "premium" })
    .select("id")
    .single();
  if (error || !data?.id)
    throw new Error("Não foi possível reservar a utilização Pro. Tenta novamente.");
  return { usageCounterId: Number(data.id) };
}

async function releaseProUsageReservation(reservation: ProUsageReservation): Promise<void> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  for (let attempt = 1; attempt <= 3; attempt++) {
    const { error } = await supabaseAdmin
      .from("usage_counters")
      .delete()
      .eq("id", reservation.usageCounterId)
      .eq("kind", "pro_itinerary");
    if (!error) return;
    console.error(`Unable to release Pro usage reservation (attempt ${attempt})`, error);
  }
}

function attachTravel(
  stops: PlannedStop[],
  mode: Mode,
  origin?: { lat: number; lng: number; label: string } | null,
): ProDayStop[] {
  const out: ProDayStop[] = [];
  let prev: { lat: number; lng: number } | null = null;
  if (origin) {
    out.push({
      kind: "poi",
      name: `🏠 ${origin.label}`,
      time: "",
      durationMin: 0,
      lat: origin.lat,
      lng: origin.lng,
      note: "Partida do alojamento.",
    });
    prev = { lat: origin.lat, lng: origin.lng };
  }
  for (const s of stops) {
    if (s.kind === "meal" || s.kind === "break") {
      out.push({
        // Pausa neutra: sem sugestão de local quando o utilizador excluiu restaurantes.
        kind: s.kind,
        name: s.label,
        time: fmtTime(s.startMin),
        durationMin: s.durationMin,
      });
      continue;
    }
    const poi = s.poi;
    const item: ProDayStop = {
      kind: "poi",
      name: poi.name,
      time: fmtTime(s.startMin),
      durationMin: s.durationMin,
      lat: poi.lat,
      lng: poi.lng,
      placeId: poi.id,
    };
    if (prev) {
      const m = haversineMeters(prev, poi);
      item.travelFromPrev = {
        meters: Math.round(m),
        minutes: travelMinutes(m, mode),
        label: modeLabel(mode),
      };
    }
    prev = { lat: poi.lat, lng: poi.lng };
    out.push(item);
  }
  return out;
}

/**
 * For car trips: group consecutive POI stops whose gap is < 600m into
 * walkable clusters, and insert a real "Estacionamento" stop (via Places
 * searchNearby) before the first POI of each cluster. Fallback: generic
 * "parking near {name}" entry with a Google Maps search link.
 *
 * Cost: at most 1 Places call per cluster (~1-3 per day).
 */
async function injectParkingStops(stops: ProDayStop[]): Promise<ProDayStop[]> {
  const { findNearestParking } = await import("./candidates.server");
  const out: ProDayStop[] = [];
  const poiIdx: number[] = [];
  stops.forEach((s, i) => {
    if (s.kind === "poi" && s.lat && s.lng && !s.name.startsWith("🏠")) poiIdx.push(i);
  });
  // Build clusters: consecutive POIs with <600m between them.
  const clusters: number[][] = [];
  let cur: number[] = [];
  for (let k = 0; k < poiIdx.length; k++) {
    const idx = poiIdx[k];
    if (cur.length === 0) {
      cur.push(idx);
      continue;
    }
    const prev = stops[cur[cur.length - 1]];
    const now = stops[idx];
    const d = haversineMeters({ lat: prev.lat!, lng: prev.lng! }, { lat: now.lat!, lng: now.lng! });
    if (d < 600) cur.push(idx);
    else {
      clusters.push(cur);
      cur = [idx];
    }
  }
  if (cur.length) clusters.push(cur);
  // Only inject parking for clusters with 2+ POIs (single stop → park at the door).
  const insertAt = new Map<number, ProDayStop>();
  for (const c of clusters) {
    if (c.length < 2) continue;
    const first = stops[c[0]];
    if (!first.lat || !first.lng) continue;
    const parking = await findNearestParking(first.lat, first.lng);
    const stop: ProDayStop = parking
      ? {
          kind: "parking",
          name: `🅿️ ${parking.name}`,
          time: first.time,
          durationMin: 0,
          lat: parking.lat,
          lng: parking.lng,
          placeId: parking.id,
          note: `Estaciona aqui — próximas ${c.length} paragens fazem-se a pé (~3 min).`,
        }
      : {
          kind: "parking",
          name: `🅿️ Estacionamento próximo — ${first.name}`,
          time: first.time,
          durationMin: 0,
          lat: first.lat,
          lng: first.lng,
          note: `Procura um parque perto e visita as próximas ${c.length} paragens a pé.`,
        };
    insertAt.set(c[0], stop);
  }
  for (let i = 0; i < stops.length; i++) {
    const inj = insertAt.get(i);
    if (inj) out.push(inj);
    out.push(stops[i]);
  }
  return out;
}

/**
 * Dá nome real aos estacionamentos criados pelo motor multimodal.
 * Uma falha aqui nunca impede o roteiro: fica o marcador genérico.
 */
async function enrichParkingStops(stops: ProDayStop[]): Promise<ProDayStop[]> {
  const targets = stops.filter((s) => s.kind === "parking" && s.lat && s.lng).slice(0, 3);
  if (targets.length === 0) return stops;
  try {
    const { findNearestParking } = await import("./candidates.server");
    for (const stop of targets) {
      const found = await findNearestParking(stop.lat as number, stop.lng as number);
      if (!found) continue;
      stop.name = `🅿️ ${found.name}`;
      stop.lat = found.lat;
      stop.lng = found.lng;
      stop.placeId = found.id;
    }
  } catch (error) {
    console.warn("parking enrichment failed", error);
  }
  return stops;
}

/**
 * Move POIs from oversized clusters to undersized ones (nearest first)
 * so every day has at least `minPerCluster` stops. Prevents "Day 1 = 1 POI"
 * when k-means splits geography unevenly.
 */
function rebalanceClusters(clusters: Poi[][], minPerCluster: number): Poi[][] {
  const arr = clusters.map((c) => [...c]);
  let guard = 0;
  while (guard++ < 200) {
    const smallIdx = arr.reduce((best, c, i) => (c.length < arr[best].length ? i : best), 0);
    const bigIdx = arr.reduce((best, c, i) => (c.length > arr[best].length ? i : best), 0);
    if (arr[smallIdx].length >= minPerCluster) break;
    if (arr[bigIdx].length <= minPerCluster + 1) break;
    // Pick the POI in the big cluster nearest to the small cluster's centroid
    // (or to any POI if empty).
    const target = arr[smallIdx][0] ?? arr[bigIdx][0];
    let bestPoi = 0,
      bd = Infinity;
    for (let i = 0; i < arr[bigIdx].length; i++) {
      const d = haversineMeters(arr[bigIdx][i], target);
      if (d < bd) {
        bd = d;
        bestPoi = i;
      }
    }
    const [moved] = arr[bigIdx].splice(bestPoi, 1);
    arr[smallIdx].push(moved);
  }
  return arr;
}

type CityAnchor = {
  name: string;
  lat: number;
  lng: number;
  days: number;
  origin?: { lat: number; lng: number; label: string } | null;
};

/**
 * Distribui POIs reais por todos os dias de uma base. Um cluster vazio recebe
 * pontos dos clusters maiores — nunca textos genéricos nem repetições.
 */
function fillEmptyClusters(clusters: Poi[][], dayCount: number): Poi[][] {
  const out = clusters.slice(0, Math.max(dayCount, clusters.length)).map((c) => [...c]);
  while (out.length < dayCount) out.push([]);
  const seen = new Set<string>();
  for (const cluster of out) for (const poi of cluster) seen.add(poi.id);
  let guard = out.length * 20;
  for (let i = 0; i < dayCount; i++) {
    while (out[i].length === 0 && guard-- > 0) {
      const donor = out.reduce((best, cluster) => (cluster.length > best.length ? cluster : best), out[0]);
      if (donor.length < 2) break;
      const moved = donor.pop();
      if (!moved) break;
      out[i].push(moved);
    }
  }
  void seen;
  return out;
}

async function buildDays(
  cityAnchors: CityAnchor[],
  interests: string[],
  transport: Mode,
  pace: "slow" | "normal" | "intense",
  mood: string | undefined,
  returnTo?: { lat: number; lng: number; label: string } | null,
  prefetchedCandidates?: Map<string, Poi[]>,
  negativePreferences?: NegativePreferences | null,
  mealPreference: MealPreference = "restaurant",
  transportPlan?: MultimodalPlan | null,
): Promise<ProDay[]> {
  const { fetchCandidates } = await import("./candidates.server");
  const { narrateDay } = await import("./narrator.server");
  const days: ProDay[] = [];
  let dayIndex = 1;
  const totalPlannedDays = cityAnchors.reduce((s, a) => s + a.days, 0);
  for (let ci = 0; ci < cityAnchors.length; ci++) {
    const anchor = cityAnchors[ci];
    const stopsPerDay = pace === "intense" ? 8 : pace === "slow" ? 4 : 6;
    const target = anchor.days * stopsPerDay;
    const searchAnchor = anchor.origin
      ? { name: anchor.name, lat: anchor.origin.lat, lng: anchor.origin.lng }
      : { name: anchor.name, lat: anchor.lat, lng: anchor.lng };
    const prefetched = prefetchedCandidates?.get(anchor.name);
    const candidates =
      prefetched && prefetched.length >= Math.min(target, anchor.days * 3)
        ? prefetched
        : await fetchCandidates(searchAnchor, interests, target, negativePreferences, undefined, {
            days: anchor.days,
          });
    if (candidates.length === 0) {
      for (let i = 0; i < anchor.days; i++)
        days.push({ day: dayIndex++, city: anchor.name, stops: [] });
      continue;
    }
    let clusters = kmeans(candidates, Math.min(anchor.days, candidates.length));
    while (clusters.length < anchor.days) clusters.push([]);
    // Rebalance so no day is starved of POIs.
    const minPerCluster = Math.max(3, Math.floor((candidates.length / anchor.days) * 0.6));
    clusters = rebalanceClusters(clusters, minPerCluster);
    // Nenhum dia de uma base pode ficar vazio: quando um cluster fica sem
    // POIs, recebe locais reais (não repetidos) dos clusters maiores.
    clusters = fillEmptyClusters(clusters, anchor.days);
    // Day 1 = cluster nearest to origin (usually hotel).
    if (anchor.origin) {
      const o = anchor.origin;
      clusters.sort((a, b) => {
        const da = a.length ? haversineMeters(o, a[0]) : Infinity;
        const db = b.length ? haversineMeters(o, b[0]) : Infinity;
        return da - db;
      });
    }
    for (let i = 0; i < anchor.days; i++) {
      const isVeryLastDay = ci === cityAnchors.length - 1 && i === anchor.days - 1;
      let cluster: Poi[] = clusters[i] ?? [];
      // On the return day, trim ~2 POIs to leave time for the journey back.
      if (isVeryLastDay && returnTo && cluster.length > 3) {
        cluster = cluster.slice(0, Math.max(3, cluster.length - 2));
      }
      const startPoint = anchor.origin ?? { lat: anchor.lat, lng: anchor.lng };
      const ordered = orderStops(cluster, startPoint);
      const planned = schedule(ordered, pace, 9, {
        mealPreference:
          mealPreference === "restaurant" && isCategoryExcluded(negativePreferences, "restaurant")
            ? "free_break"
            : mealPreference,
      });
      let stops = attachTravel(planned, transport, anchor.origin ?? null);
      if (negativePreferences)
        stops = dropExcludedStops(
          [{ day: dayIndex, city: anchor.name, stops }],
          negativePreferences,
        )[0].stops;
      if (transportPlan && isMultimodalPlan(transportPlan)) {
        // Multimodal: modo por segmento + mudanças de veículo explícitas.
        stops = (await enrichParkingStops(
          planMultimodalDay(stops as never, transportPlan) as ProDayStop[],
        )) as ProDayStop[];
      } else if (transport === "car") stops = await injectParkingStops(stops);
      // Append return leg on the very last day of the trip.
      if (isVeryLastDay && returnTo) {
        const last = [...stops].reverse().find((s) => s.lat != null && s.lng != null);
        const returnStop: ProDayStop = {
          kind: "poi",
          name: `🏁 Regresso a ${returnTo.label}`,
          time: "",
          durationMin: 0,
          lat: returnTo.lat,
          lng: returnTo.lng,
          note: "Fim do roteiro — regresso ao ponto de partida (hotel/aeroporto).",
        };
        if (last?.lat != null && last.lng != null) {
          const m = haversineMeters({ lat: last.lat, lng: last.lng }, returnTo);
          returnStop.travelFromPrev = {
            meters: Math.round(m),
            minutes: travelMinutes(m, transport),
            label: modeLabel(transport),
          };
        }
        stops.push(returnStop);
      }
      const nar = await narrateDay({
        city: anchor.name,
        mood,
        stops: stops.map((s) => ({
          name: s.name,
          time: s.time,
          // Pausas neutras são narradas como momento livre, não como refeição.
          kind: s.kind === "break" ? ("experience" as const) : s.kind,
        })),
      });
      for (let idx = 0; idx < stops.length; idx++) {
        const note = nar.notes[String(idx + 1)];
        if (note && !stops[idx].note) stops[idx].note = note;
      }
      days.push({ day: dayIndex++, city: anchor.name, intro: nar.intro || undefined, stops });
    }
  }
  void totalPlannedDays;
  return days;
}

export const generateProItinerary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => GenerateInput.parse(d))
  .handler(async ({ data, context }) => {
    const explicitMultiPlan = data.multiDestinationPlan;
    if (explicitMultiPlan && !isValidMultiDestinationPlan(explicitMultiPlan)) {
      throw new Error("A distribuição dos dias pelos destinos está incompleta. Confirma os destinos e os dias — não gastaste crédito.");
    }
    // Gate gratuito: uma estrada famosa nunca inicia uma tentativa nem reserva
    // quota sem o utilizador escolher percurso completo ou troço. A própria
    // viabilidade do número de dias também é resolvida aqui.
    const preflight = preflightProGeneration({
      prompt: data.prompt,
      days: data.routeReturn === "included" ? Math.max(1, data.days - 1) : data.days,
      templateId: data.templateId,
      templateMode: data.templateMode,
      startRegion: data.cities[0] ?? data.accommodation ?? "",
    });
    if (!preflight.canStartGeneration || !preflight.canReserveQuota) {
      throw new Error(`${preflight.message ?? "Falta confirmar o tipo de percurso."}\n\nNão gastaste crédito.`);
    }

    await ensurePro(context.supabase, context.userId);
    const generationAttempt = await beginGenerationAttemptInternal("pro", data.requestId, [
      data.prompt,
      data.days,
      (data.cities ?? []).join(","),
      data.transport,
      data.pace,
      (data.interests ?? []).join(","),
      // A escolha de reparação é uma tentativa nova e legítima: entra na
      // impressão digital para não ser bloqueada como pedido duplicado.
      data.repairStrategy ?? "",
      data.templateId ?? "",
      data.templateMode ?? "",
    ]);
    if (!generationAttempt.ok) throw new Error(generationGuardMessage(generationAttempt));
    let reservation: ProUsageReservation | undefined;
    let isAuthorizedRepair = false;
    if (data.repairStrategy && data.repairOfId) {
      const { data: draft } = await context.supabase
        .from("pro_itineraries")
        .select("id,status")
        .eq("id", data.repairOfId)
        .eq("user_id", context.userId)
        .eq("status", "draft")
        .maybeSingle();
      isAuthorizedRepair = Boolean(draft?.id);
    }
    if (data.repairStrategy && !isAuthorizedRepair) {
      // A referência pode ter-se perdido (sessão, recarregamento, roteiro
      // nunca persistido). Uma reparação apresentada ao utilizador nunca pode
      // terminar em beco sem saída: se existir uma tentativa recente deste
      // utilizador, reconstruímos gratuitamente com o pedido original.
      const since = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
      const { data: recent } = await context.supabase
        .from("pro_itineraries")
        .select("id")
        .eq("user_id", context.userId)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      isAuthorizedRepair = Boolean(recent?.id);
    }
    if (!isAuthorizedRepair) {
      try {
        reservation = await enforceProRateLimit(context.userId);
      } catch (error) {
        await finishGenerationAttemptInternal(generationAttempt, "quota_denied");
        throw error;
      }
      const attached = await attachGenerationQuotaInternal(generationAttempt, {
        usageCounterId: String(reservation.usageCounterId),
      });
      if (!attached) {
        await releaseProUsageReservation(reservation);
        await finishGenerationAttemptInternal(generationAttempt, "technical_failure", {
          failureCode: "pro_quota_attach_failed",
        });
        throw new Error(
          "Não foi possível iniciar a geração com segurança. Não contou para o teu limite.",
        );
      }
    }
    try {
      // Contrato universal do pedido ATUAL: inclusões, exclusões, itens
      // nomeados e refeições numa só estrutura. Reconstruído do texto desta
      // pesquisa — nunca herda nada de pesquisas anteriores.
      // Catálogo administrável: as rotas curadas geridas no Painel do Fundador
      // entram em memória antes de resolver o pedido; a procura é contada sem
      // qualquer dado pessoal (só a chave normalizada e o resultado).
      const { loadCuratedRoutesIntoCatalog, recordRouteDemand } = await import(
        "@/lib/catalog/catalog-sync.server"
      );
      const { resolveRouteRequest } = await import("@/lib/catalog/route-resolution");
      await loadCuratedRoutesIntoCatalog();
      await recordRouteDemand(resolveRouteRequest({ text: data.prompt, scale: "regional" }), {
        term: data.prompt,
        lang: (data as any).lang ?? undefined,
        // Semente apenas para hash opaco: nunca é guardada em claro.
        sessionSeed: context.userId,
      });

      const corridor =
        preflight.corridor ?? corridorForTemplate(data.templateId) ?? findCorridorInText(data.prompt);
      const tripPreferences = buildTripPreferences({
        text: data.prompt,
        formInterests: data.interests ?? [],
        mealChoice: data.mealPreference ?? null,
        corridorId: corridor?.id ?? null,
        maxDeviationKm: corridor?.maxDeviationKm ?? null,
      });
      const primaryObjective = detectPrimaryObjective(data.prompt);
      const mealPreference: MealPreference = data.mealPreference ?? tripPreferences.mealPreference;
      const negativePrefs: NegativePreferences = toNegativePreferences(tripPreferences);
      void parseNegativePreferences;
      const { parseIntent } = await import("./intent-parser.server");
      const intent = await parseIntent(data.prompt, data.days);

      // Reconcile intent with explicit form input.
      // Transport = form always wins (explicit toggle).
      // Pace = intent overrides ONLY when form is still at the "normal" default AND intent has a stronger signal.
      // Interests = MERGE intent + form (dedup), so if user writes "natureza e paisagens" the pipeline actually fetches nature POIs even with default toggles.
      let transport: Mode = data.transport ?? intent.transport;
      // Plano multimodal: só ativa com modos locais escolhidos no formulário
      // ou pedidos explicitamente no texto. Caso contrário, nada muda.
      const parsedTransportPlan = parseTransportPlan(
        data.prompt,
        (transport as TransportMode) ?? "walk",
      );
      const primaryMode: TransportMode =
        parsedTransportPlan.primary === "motorcycle" && transport === "car"
          ? "motorcycle"
          : (transport as TransportMode);
      const formSecondary = (data.secondaryModes ?? []).filter((mode) => mode !== primaryMode);
      const transportPlan: MultimodalPlan = formSecondary.length
        ? { primary: primaryMode, secondary: formSecondary as TransportMode[], hybrid: true }
        : parsedTransportPlan.hybrid && parsedTransportPlan.primary === primaryMode
          ? { primary: primaryMode, secondary: parsedTransportPlan.secondary, hybrid: true }
          : { primary: primaryMode, secondary: [], hybrid: false };
      const pace =
        data.pace === "normal" && intent.pace && intent.pace !== "normal"
          ? intent.pace
          : (data.pace ?? intent.pace);
      const mergedInterests = Array.from(
        new Set([...(data.interests ?? []), ...(intent.interests ?? [])]),
      )
        .filter(Boolean)
        .filter((i) => {
          const mapped = INTEREST_TO_EXCLUDED[i];
          return !mapped || !isCategoryExcluded(negativePrefs, mapped);
        });
      // Pedidos positivos são vinculativos e exclusões vencem interesses.
      const contractInterests = interestsFromPreferences(tripPreferences, mergedInterests);
      const objectiveInterests =
        primaryObjective?.exclusive && primaryObjective.category === "restaurant"
          ? Array.from(new Set(["food", ...contractInterests]))
          : contractInterests;
      const interests =
        objectiveInterests.length > 0 ? objectiveInterests : ["cultural", "viewpoint"];
      // Contrato Pro multi-destino: os dias vêm SEMPRE da soma das paragens
      // escritas pelo utilizador, nunca do primeiro número nem do formulário.
      const multiPlan = explicitMultiPlan ?? parseMultiDestinationPlan(data.prompt);
      const totalDays = multiPlan
        ? multiPlan.totalDays
        : data.routeReturn === "included"
          ? Math.max(1, data.days - 1)
          : (data.days || intent.totalDays || 3);

      // Esqueleto geográfico primeiro: uma experiência curada linear (estrada,
      // comboio, trilho) define as bases e a progressão diária antes de
      // qualquer atração. Só depois entram cidades do formulário/intenção.
      const activeTemplate = preflight.template;
      const skeleton =
        preflight.skeleton ??
        (activeTemplate && data.templateMode
          ? buildRouteSkeleton({
              template: activeTemplate,
              days: totalDays,
              mode: data.templateMode,
              startRegion: data.cities[0] ?? data.accommodation ?? "",
            })
          : null);

      // Merge cities: skeleton > explicit form cities > parsed cities > prompt.
      let cityAnchors: Array<{
        name: string;
        lat: number;
        lng: number;
        days: number;
        origin?: { lat: number; lng: number; label: string } | null;
      }> = [];
      if (skeleton) {
        if (!skeleton.feasible) {
          throw new Error(
            `${skeleton.reason ?? "Os dias disponíveis não chegam para o percurso completo."}\n\nEscolhe mais dias ou seleciona "Apenas um troço" — não gastaste crédito.`,
          );
        }
        cityAnchors = skeleton.cities.map((name) => ({ name, lat: 0, lng: 0, days: 1 }));
        // O meio de transporte da experiência é vinculativo: nunca comboio
        // numa estrada, nunca carro num trilho.
        transport = skeleton.transport;
      } else {
        const seeds = resolveCityAnchors({
          formCities: data.cities,
          intentCities: intent.cities.map((c) => ({ name: c.name, days: c.days })),
          prompt: data.prompt,
          totalDays,
        });
        if (seeds.length === 0) {
          throw new Error(
            "Não consegui identificar a cidade ou localidade. Indica pelo menos o destino.",
          );
        }
        const intentByName = new Map(
          intent.cities.map((c) => [c.name.trim().toLocaleLowerCase(), c]),
        );
        cityAnchors = seeds.map((seed) => {
          const match = intentByName.get(seed.name.trim().toLocaleLowerCase());
          return {
            name: sanitizeDestinationName(seed.name) ?? seed.name,
            lat: match?.lat ?? 0,
            lng: match?.lng ?? 0,
            days: seed.days,
          };
        });
        if (multiPlan) {
          // Ordem e dias por destino são vinculativos.
          cityAnchors = multiPlan.stops.map((stop) => {
            const match = intentByName.get(stop.destination.trim().toLocaleLowerCase());
            return {
              name: stop.destination,
              lat: match?.lat ?? 0,
              lng: match?.lng ?? 0,
              days: stop.days,
            };
          });
        }
      }

      // Assertion vinculativa ANTES de qualquer chamada paga (Places/IA):
      // as bases e os dias têm de ser exatamente os do contrato escrito.
      if (multiPlan) {
        const mismatches = contractAnchorMismatches(multiPlan, cityAnchors);
        if (mismatches.length > 0) {
          throw new Error(
            `A distribuição pedida não foi respeitada (${mismatches.join("; ")}). Não gastaste crédito — tenta novamente indicando os dias por destino.`,
          );
        }
      }



      const key = process.env.GOOGLE_MAPS_SERVER_API_KEY;
      async function geocode(query: string): Promise<{ lat: number; lng: number } | null> {
        if (!key) return null;
        try {
          const r = await cachedPlacesFetch("https://places.googleapis.com/v1/places:searchText", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": key,
              "X-Goog-FieldMask": "places.location",
            },
            body: JSON.stringify({ textQuery: query, maxResultCount: 1 }),
            signal: AbortSignal.timeout(20_000),
          }, { kind: "pro_geocode", ttlMs: PLACES_TTL.geocodeForward, generationId: data.requestId });
          if (!r.ok) return null;
          const j = (await r.json()) as {
            places?: Array<{ location?: { latitude?: number; longitude?: number } }>;
          };
          const loc = j.places?.[0]?.location;
          if (loc?.latitude && loc?.longitude) return { lat: loc.latitude, lng: loc.longitude };
        } catch {
          /* skip */
        }
        return null;
      }

      for (const a of cityAnchors) {
        if (a.lat === 0 && a.lng === 0) {
          // Alguns destinos só resolvem com contexto ("Creta" falha, "ilha de
          // Creta" resolve). Sem coordenadas, a base ficaria sem POIs reais.
          for (const query of [a.name, `ilha de ${a.name}`, `região de ${a.name}`, `${a.name} turismo`]) {
            const g = await geocode(query);
            if (g) {
              a.lat = g.lat;
              a.lng = g.lng;
              break;
            }
          }
        }
      }


      // Origin resolution + city reorder.
      // If we have an origin reference (accommodation or geolocation), reorder
      // cities so the CLOSEST one is Day 1. Prevents "user is in Caracal, typed
      // Caracal+Craiova, gets Day 1 in Craiova".
      const accommodation = data.accommodation?.trim();
      let originRef: { lat: number; lng: number; label: string } | null = null;
      if (accommodation) {
        const g = await geocode(accommodation);
        if (g) originRef = { lat: g.lat, lng: g.lng, label: accommodation };
      } else if (data.originCoords) {
        originRef = {
          lat: data.originCoords.lat,
          lng: data.originCoords.lng,
          label: "A tua localização atual",
        };
      }

      if (originRef && cityAnchors.length > 1 && !skeleton && !multiPlan) {
        cityAnchors.sort((a, b) => {
          const da =
            a.lat === 0 && a.lng === 0
              ? Infinity
              : haversineMeters(originRef!, { lat: a.lat, lng: a.lng });
          const db =
            b.lat === 0 && b.lng === 0
              ? Infinity
              : haversineMeters(originRef!, { lat: b.lat, lng: b.lng });
          return da - db;
        });
      }

      if (cityAnchors.length > 0) {
        const first = cityAnchors[0];
        if (originRef) {
          const d =
            first.lat !== 0 || first.lng !== 0
              ? haversineMeters(originRef, { lat: first.lat, lng: first.lng })
              : 0;
          // Accommodation always attaches to the (now nearest) city.
          // Geolocation only if within 30km — otherwise use city hub.
          if (accommodation || d < 30000) {
            first.origin = originRef;
          }
        }
        if (!first.origin) {
          const { findCityHub } = await import("./city-hubs");
          const hub = findCityHub(first.name);
          if (hub) first.origin = { lat: hub.lat, lng: hub.lng, label: hub.label };
        }
      }

      // Weight day allocation by POI density per city.
      // Cities with more candidates get proportionally more days (sqrt curve so
      // one giant city doesn't eat everything). Min 1 day per city.
      const { fetchCandidates } = await import("./candidates.server");
      const stopsPerDay = pace === "intense" ? 8 : pace === "slow" ? 4 : 6;
      const candidatesByCity = new Map<string, Poi[]>();
      if (cityAnchors.length > 1 && !skeleton && !multiPlan) {
        const preTarget = Math.max(12, stopsPerDay * 2);
        await Promise.all(
          cityAnchors.map(async (a) => {
            if (a.lat === 0 && a.lng === 0) return;
            const searchAnchor = a.origin
              ? { name: a.name, lat: a.origin.lat, lng: a.origin.lng }
              : { name: a.name, lat: a.lat, lng: a.lng };
            try {
              const c = await fetchCandidates(searchAnchor, interests, preTarget);
              candidatesByCity.set(a.name, c);
            } catch {
              /* skip */
            }
          }),
        );
        const weights = cityAnchors.map((a) =>
          Math.sqrt(Math.max(1, candidatesByCity.get(a.name)?.length ?? 1)),
        );
        const wSum = weights.reduce((s, w) => s + w, 0);
        if (wSum > 0) {
          const raw = weights.map((w) => (w / wSum) * totalDays);
          const floored = raw.map((r) => Math.max(1, Math.floor(r)));
          let assigned = floored.reduce((s, n) => s + n, 0);
          // Distribute leftover days to cities with highest fractional part.
          const fracs = raw.map((r, i) => ({ i, f: r - Math.floor(r) })).sort((a, b) => b.f - a.f);
          let k = 0;
          while (assigned < totalDays && k < fracs.length * 5) {
            floored[fracs[k % fracs.length].i] += 1;
            assigned += 1;
            k += 1;
          }
          // Trim if we overshot (unlikely).
          while (assigned > totalDays) {
            const maxI = floored.indexOf(Math.max(...floored));
            if (floored[maxI] <= 1) break;
            floored[maxI] -= 1;
            assigned -= 1;
          }
          cityAnchors.forEach((a, i) => {
            a.days = floored[i];
          });
        }
      }

      // Pré-recolha de candidatos sem redistribuir dias (contrato vinculativo).
      if (multiPlan && cityAnchors.length > 1) {
        await Promise.all(
          cityAnchors.map(async (a) => {
            if (a.lat === 0 && a.lng === 0) return;
            try {
              candidatesByCity.set(
                a.name,
                await fetchCandidates(
                  { name: a.name, lat: a.lat, lng: a.lng },
                  interests,
                  Math.max(12, a.days * stopsPerDay),
                  negativePrefs,
                  undefined,
                  { days: a.days },
                ),
              );
            } catch {
              /* skip */
            }
          }),
        );
      }

      let days = await buildDays(
        cityAnchors,
        interests,
        transport,
        pace,
        intent.mood,
        originRef,
        candidatesByCity,
        negativePrefs,
        mealPreference,
        transportPlan,
      );

      // ── Experiências lineares de estrada ──
      // Numa estrada curada (Nacional 2, Route 66, NC500…) o dia é a etapa
      // entre duas bases, não um dia de turismo parado numa cidade. Este passo
      // é genérico por TIPO de experiência, nunca por destino.
      if (skeleton && activeTemplate) {
        const { applyRoadStageDays } = await import("./road-stage-days");
        const coords = new Map<string, { lat: number; lng: number }>();
        for (const anchor of cityAnchors) {
          if (anchor.lat && anchor.lng) {
            coords.set(
              anchor.name.normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase(),
              { lat: anchor.lat, lng: anchor.lng },
            );
          }
        }
        for (const point of corridor?.waypoints ?? []) {
          coords.set(
            point.name.normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase(),
            { lat: point.lat, lng: point.lng },
          );
        }
        days = applyRoadStageDays({
          days,
          skeleton,
          template: activeTemplate,
          coords,
          corridorWaypoints: corridor?.waypoints,
          mealPreference,
        });
        if (corridor) {
          // Modo estrito: nada fora do traçado oficial entra na etapa e os
          // pontos de estrada seguem sempre a progressão do corredor.
          const { enforceStrictCorridorDays } = await import("./strict-corridor");
          days = enforceStrictCorridorDays(days as any, corridor) as typeof days;
        }

        if (data.routeReturn === "included" && days.length > 0) {
          const first = corridor?.waypoints[0];
          const last = corridor?.waypoints.at(-1);
          if (first && last) {
            days.push({
              day: days.length + 1,
              city: `${last.name} → ${first.name} (regresso)`,
              intro: `Dia reservado ao regresso de ${last.name} a ${first.name}. Este trajeto não faz parte do corredor ${activeTemplate.name}.`,
              stops: [
                {
                  kind: "transfer",
                  category: "return",
                  name: `🏁 Regresso de ${last.name} a ${first.name}`,
                  time: "09:00",
                  durationMin: 0,
                  lat: first.lat,
                  lng: first.lng,
                  note: "Regresso pela opção mais adequada; este dia fica fora das etapas da estrada curada.",
                },
              ],
            });
          }
        }
      }



      // ── Contrato Pro: interesses vinculativos + logística entre bases ──
      if (multiPlan) {
        days = enforceInterestBalance(days, interests);
        const anchorCoords = new Map<string, { lat: number; lng: number }>();
        for (const anchor of cityAnchors) {
          if (anchor.lat || anchor.lng) {
            anchorCoords.set(normalizeDestinationKey(anchor.name), { lat: anchor.lat, lng: anchor.lng });
          }
        }
        days = applyInterDestinationTransfers(days, anchorCoords);
      }

      // ── Cobertura obrigatória: títulos estáveis; o preenchimento de POIs já
      // acontece dentro dos clusters de cada base em buildDays. Nunca mover
      // paragens entre dias depois das transferências e horários estarem feitos.
      {
        const { formatDestinationTitle } = await import("./day-coverage");
        days = days.map((day) => ({ ...day, city: formatDestinationTitle(day.city) }));
      }

      // ── Locais obrigatórios ("que inclua …") ──
      // São locais, nunca cidades: resolvem-se pelo nome perto da base e entram
      // uma vez no dia mais próximo. Os horários são recalculados a seguir.
      const requestedStops = data.requiredStops ?? [];
      const missingRequiredStops: string[] = [];
      if (requestedStops.length > 0 && days.length > 0) {
        const { injectRequiredStops } = await import("./required-stop-injection");
        const key = process.env.GOOGLE_MAPS_SERVER_API_KEY;
        const base = cityAnchors[0];
        const resolved: Array<{
          requested: string;
          name: string;
          lat: number;
          lng: number;
          placeId?: string;
          isMeal?: boolean;
        }> = [];
        for (const requested of requestedStops) {
          if (!key) {
            missingRequiredStops.push(requested);
            continue;
          }
          try {
            const r = await cachedPlacesFetch(
              "https://places.googleapis.com/v1/places:searchText",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Goog-Api-Key": key,
                  "X-Goog-FieldMask":
                    "places.id,places.displayName,places.location,places.primaryType",
                },
                body: JSON.stringify({
                  textQuery: base?.name ? `${requested}, ${base.name}` : requested,
                  maxResultCount: 1,
                  ...(base?.lat || base?.lng
                    ? {
                        locationBias: {
                          circle: {
                            center: { latitude: base.lat, longitude: base.lng },
                            radius: 40000,
                          },
                        },
                      }
                    : {}),
                }),
                signal: AbortSignal.timeout(20_000),
              },
              { kind: "pro_places", ttlMs: PLACES_TTL.attractions },
            );
            const j = r.ok
              ? ((await r.json()) as {
                  places?: Array<{
                    id?: string;
                    displayName?: { text?: string };
                    location?: { latitude?: number; longitude?: number };
                    primaryType?: string;
                  }>;
                })
              : {};
            const p = j.places?.[0];
            if (p?.displayName?.text && p.location?.latitude && p.location?.longitude) {
              resolved.push({
                requested,
                name: p.displayName.text,
                lat: p.location.latitude,
                lng: p.location.longitude,
                placeId: p.id,
                isMeal: /restaurant|cafe|bar|bakery|food/u.test(p.primaryType ?? ""),
              });
            } else {
              missingRequiredStops.push(requested);
            }
          } catch {
            missingRequiredStops.push(requested);
          }
        }
        if (resolved.length > 0) {
          const result = injectRequiredStops(days as any, resolved);
          days = result.days as typeof days;
          const { enforceChronology } = await import("./chronology");
          days = enforceChronology({ transport, days } as any).plan.days as typeof days;
        }
      }



      // ── Post-processamento opcional: dias temáticos (praia/zoo/parque aquático…) ──
      // Detectados no texto livre do utilizador. Se houver datas, escolhe o
      // dia com melhor meteo (Open-Meteo) para temas outdoor. Não altera nada
      // do buildDays — apenas substitui as stops de dias específicos.
      try {
        const { detectThemedDays } = await import("./themed-days");
        const themed = detectThemedDays(data.prompt);
        if (themed.length > 0 && days.length > 0) {
          const { fetchTripWeather } = await import("./weather.server");
          // Meteo para a cidade do primeiro anchor (aproximação suficiente).
          const firstCity = cityAnchors[0];
          let weather: Awaited<ReturnType<typeof fetchTripWeather>> = [];
          if (data.startDate && data.endDate && firstCity?.lat && firstCity?.lng) {
            weather = await fetchTripWeather(
              firstCity.lat,
              firstCity.lng,
              data.startDate,
              data.endDate,
            );
          }
          const usedDays = new Set<number>();
          for (const t of themed) {
            // Dia alvo: melhor meteo para outdoor, ou 1º dia ainda livre.
            let targetDayIdx = -1;
            if (t.outdoor && weather.length > 0) {
              const ranked = weather.map((w, i) => ({ i, s: w.score })).sort((a, b) => b.s - a.s);
              for (const r of ranked) {
                if (r.i < days.length && !usedDays.has(r.i)) {
                  targetDayIdx = r.i;
                  break;
                }
              }
            }
            if (targetDayIdx < 0) {
              for (let i = 0; i < days.length; i++) {
                if (!usedDays.has(i)) {
                  targetDayIdx = i;
                  break;
                }
              }
            }
            if (targetDayIdx < 0) break;
            const targetDay = days[targetDayIdx];
            // Procura POI temático na cidade desse dia.
            const anchorForDay = cityAnchors.find((c) => c.name === targetDay.city) ?? firstCity;
            if (!anchorForDay?.lat || !anchorForDay?.lng) continue;
            const key = process.env.GOOGLE_MAPS_SERVER_API_KEY;
            if (!key) continue;
            let themedPoi: { id: string; name: string; lat: number; lng: number } | null = null;
            try {
              const r = await cachedPlacesFetch("https://places.googleapis.com/v1/places:searchText", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Goog-Api-Key": key,
                  "X-Goog-FieldMask": "places.id,places.displayName,places.location",
                },
                body: JSON.stringify({
                  textQuery: `${t.keyword} in ${anchorForDay.name}`,
                  maxResultCount: 1,
                  locationBias: {
                    circle: {
                      center: { latitude: anchorForDay.lat, longitude: anchorForDay.lng },
                      radius: 40000,
                    },
                  },
                }),
                signal: AbortSignal.timeout(20_000),
              }, { kind: "pro_themed", ttlMs: PLACES_TTL.attractions, generationId: data.requestId });
              if (r.ok) {
                const j = (await r.json()) as {
                  places?: Array<{
                    id?: string;
                    displayName?: { text?: string };
                    location?: { latitude?: number; longitude?: number };
                  }>;
                };
                const p = j.places?.[0];
                if (p?.id && p.displayName?.text && p.location?.latitude && p.location?.longitude) {
                  themedPoi = {
                    id: p.id,
                    name: p.displayName.text,
                    lat: p.location.latitude,
                    lng: p.location.longitude,
                  };
                }
              }
            } catch {
              /* ignora — fica dia normal */
            }
            if (!themedPoi) continue;
            const wInfo = weather[targetDayIdx];
            const wNote = wInfo
              ? ` (meteo previsto: ${Math.round(wInfo.tmax)}°C, ${wInfo.precip.toFixed(1)}mm chuva)`
              : "";
            targetDay.intro = `☀️ Dia dedicado a ${t.label}. Aproveita o dia todo por aqui${wNote}.`;
            targetDay.stops = [
              {
                kind: "poi",
                name: `🎯 ${themedPoi.name}`,
                time: "10:00",
                durationMin: 360,
                lat: themedPoi.lat,
                lng: themedPoi.lng,
                placeId: themedPoi.id,
                note: `Dia temático — planeia levar tudo o que precisas para ${t.label}.`,
              },
            ];
            usedDays.add(targetDayIdx);
          }
        }
      } catch (e) {
        console.warn("themed-days post-processing failed", e);
      }

      let plan: ProPlan = {
        title: `Roteiro Pro — ${cityAnchors.map((c) => c.name).join(" · ")}`,
        status: "ready",
        transport,
        pace,
        mood: intent.mood,
        days,
        ...(isMultimodalPlan(transportPlan)
          ? {
              transportPlan: {
                primary: transportPlan.primary,
                secondary: [...(transportPlan.secondary ?? [])],
                hybrid: true,
              },
              allowedModes: [transportPlan.primary, ...(transportPlan.secondary ?? [])],
            }
          : {}),
      };

      // Estradas míticas que se fazem de mota: o cálculo no Maps continua a
      // ser de automóvel, mas as indicações ao viajante são dadas de mota.
      if (activeTemplate?.defaultRideMode === "motorcycle") {
        plan = {
          ...plan,
          rideMode: "motorcycle",
          days: plan.days.map((day) => ({
            ...day,
            stops: day.stops.map((stop) =>
              stop.travelFromPrev
                ? { ...stop, travelFromPrev: { ...stop.travelFromPrev, label: "de mota" } }
                : stop,
            ),
          })),
        };
      }

      // ── Sugestões extra (hotel / restaurantes imperdíveis) ──
      // Só quando o utilizador as pede explicitamente no texto livre.
      // Não substituem paragens do roteiro; aparecem numa secção à parte.
      try {
        const suggestions: ProSuggestions = {};
        if (intent.brief) suggestions.brief = intent.brief;
        const mapsKey = process.env.GOOGLE_MAPS_SERVER_API_KEY;
        const searchAnchor = cityAnchors[0];
        if (mapsKey && searchAnchor) {
          const hotelCoord =
            originRef ??
            (searchAnchor.lat || searchAnchor.lng
              ? { lat: searchAnchor.lat, lng: searchAnchor.lng }
              : null);
          const hotelBias = hotelCoord
            ? {
                circle: {
                  center: { latitude: hotelCoord.lat, longitude: hotelCoord.lng },
                  radius: 25000,
                },
              }
            : undefined;
          async function placesTextSearch(
            textQuery: string,
            max: number,
            includedType?: string,
            bias?: { lat: number; lng: number },
          ) {
            const body: Record<string, unknown> = { textQuery, maxResultCount: max };
            const activeBias = bias
              ? { circle: { center: { latitude: bias.lat, longitude: bias.lng }, radius: 25000 } }
              : hotelBias;
            if (activeBias) body.locationBias = activeBias;
            if (includedType) body.includedType = includedType;
            body.languageCode = "pt";
            const r = await cachedPlacesFetch("https://places.googleapis.com/v1/places:searchText", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": mapsKey!,
                "X-Goog-FieldMask":
                  "places.id,places.displayName,places.location,places.formattedAddress,places.rating",
              },
              body: JSON.stringify(body),
              signal: AbortSignal.timeout(20_000),
            }, {
              kind: includedType === "lodging" ? "pro_lodging" : includedType === "restaurant" ? "pro_dining" : "pro_places",
              ttlMs: includedType === "lodging" ? PLACES_TTL.lodging : PLACES_TTL.dining,
              generationId: data.requestId,
            });
            if (!r.ok)
              return [] as Array<{
                id?: string;
                displayName?: { text?: string };
                location?: { latitude?: number; longitude?: number };
                formattedAddress?: string;
                rating?: number;
              }>;
            const j = (await r.json()) as {
              places?: Array<{
                id?: string;
                displayName?: { text?: string };
                location?: { latitude?: number; longitude?: number };
                formattedAddress?: string;
                rating?: number;
              }>;
            };
            return j.places ?? [];
          }
          // Hotel — só se o utilizador pediu recomendação e NÃO indicou já alojamento.
          if (intent.wantsHotel && !accommodation) {
            const prefs = intent.hotelPreferences?.trim();
            const q = `${prefs ? prefs + " " : ""}hotel in ${searchAnchor.name}`;
            const results = await placesTextSearch(q, 1, "lodging");
            const p = results[0];
            if (p?.id && p.displayName?.text && p.location?.latitude && p.location?.longitude) {
              suggestions.hotel = {
                id: p.id,
                name: p.displayName.text,
                lat: p.location.latitude,
                lng: p.location.longitude,
                address: p.formattedAddress,
                rating: p.rating ?? null,
                preferences: prefs,
                note: prefs ? `Escolhido a pensar em "${prefs}".` : undefined,
              };
            }
          }
          // ── Aluguer (opcional) ──
          // Só quando o utilizador pede. Empresas reais perto do alojamento ou
          // do primeiro ponto de utilização: sem preços, horários nem stock.
          const rentalMode: TransportMode | null = data.rentalSuggestions
            ? ["car", "motorcycle", "bike", "scooter"].includes(transportPlan.primary)
              ? transportPlan.primary
              : ((transportPlan.secondary ?? []).find((mode) =>
                  ["car", "motorcycle", "bike", "scooter"].includes(mode),
                ) ?? null)
            : null;
          if (rentalMode) {
            try {
              const query = {
                car: "rent a car",
                motorcycle: "motorcycle rental",
                bike: "bicycle rental",
                scooter: "scooter rental",
              }[rentalMode as "car" | "motorcycle" | "bike" | "scooter"];
              const results = await placesTextSearch(
                `${query} em ${searchAnchor.name}`,
                3,
                undefined,
                hotelCoord ?? undefined,
              );
              const rentals = results
                .filter((p) => p.id && p.displayName?.text && p.location?.latitude && p.location?.longitude)
                .map((p) => ({
                  id: p.id as string,
                  name: p.displayName!.text as string,
                  lat: p.location!.latitude as number,
                  lng: p.location!.longitude as number,
                  address: p.formattedAddress,
                  rating: p.rating ?? null,
                  mode: rentalMode,
                  pickupZone: searchAnchor.name,
                  note: "Levantamento e devolução sugeridos nesta zona. Confirma disponibilidade e condições diretamente com a empresa.",
                }));
              if (rentals.length > 0) suggestions.rentals = rentals;
            } catch (error) {
              // Nunca bloqueia o roteiro.
              console.warn("rental suggestions failed", error);
            }
          }

          // Restaurantes reais para todas as pausas de refeição. A pesquisa é
          // feita durante a geração e os dados ficam persistidos para o PDF.
          if (
            mealPreference === "restaurant" &&
            !isCategoryExcluded(negativePrefs, "restaurant") &&
            plan.days.some((day) => day.stops.some((stop) => stop.kind === "meal"))
          ) {
            const hints = intent.restaurantHints?.trim();
            const restaurantResults = await Promise.all(
              cityAnchors.map((anchor) =>
                placesTextSearch(
                  `${hints ? hints + " " : "restaurantes bem avaliados "}em ${anchor.name}`,
                  Math.max(2, anchor.days * 2),
                  "restaurant",
                  { lat: anchor.lat, lng: anchor.lng },
                ),
              ),
            );
            const results = restaurantResults.flat();
            const picks: ProSuggestion[] = [];
            const seen = new Set<string>();
            for (const p of results) {
              if (!p.id || !p.displayName?.text || !p.location?.latitude || !p.location?.longitude)
                continue;
              if (seen.has(p.id)) continue;
              seen.add(p.id);
              picks.push({
                id: p.id,
                name: p.displayName.text,
                lat: p.location.latitude,
                lng: p.location.longitude,
                address: p.formattedAddress,
                rating: p.rating ?? null,
              });
              if (picks.length >= Math.min(12, Math.max(4, plan.days.length * 2))) break;
            }
            if (picks.length > 0) suggestions.restaurants = picks;
          }

          // ── Dormida real no fim de cada etapa ──
          // Genérico: qualquer paragem de alojamento sem sítio real (as etapas
          // de estrada criam-nas) passa a ter hotel verificado, com morada e
          // coordenadas para o mapa e para o PDF.
          const lodgingCities = new Map<string, string>();
          for (const day of plan.days) {
            for (const stop of day.stops) {
              if (stop.category !== "lodging" || stop.placeId) continue;
              const city = (day.city.split("→").pop() ?? day.city).trim();
              if (city) lodgingCities.set(city.toLocaleLowerCase(), city);
            }
          }
          if (lodgingCities.size > 0) {
            const hotelByCity = new Map<
              string,
              { id: string; name: string; lat: number; lng: number; address?: string }
            >();
            await Promise.all(
              Array.from(lodgingCities.entries()).map(async ([key, city]) => {
                try {
                  const found = await placesTextSearch(`hotel em ${city}`, 1, "lodging");
                  const p = found[0];
                  if (p?.id && p.displayName?.text && p.location?.latitude && p.location?.longitude) {
                    hotelByCity.set(key, {
                      id: p.id,
                      name: p.displayName.text,
                      lat: p.location.latitude,
                      lng: p.location.longitude,
                      address: p.formattedAddress,
                    });
                  }
                } catch {
                  // sem hotel verificado a paragem fica como está
                }
              }),
            );
            if (hotelByCity.size > 0) {
              plan.days = plan.days.map((day) => ({
                ...day,
                stops: day.stops.map((stop) => {
                  if (stop.category !== "lodging" || stop.placeId) return stop;
                  const city = (day.city.split("→").pop() ?? day.city).trim();
                  const hotel = hotelByCity.get(city.toLocaleLowerCase());
                  if (!hotel) return stop;
                  return {
                    ...stop,
                    name: `🛏️ Dormida em ${city} — ${hotel.name}`,
                    lat: hotel.lat,
                    lng: hotel.lng,
                    placeId: hotel.id,
                    note: `${hotel.address ?? `Alojamento em ${city}`}. Fim da etapa de hoje: confirma disponibilidade antes de chegares.`,
                  };
                }),
              }));
            }
          }
        }
        if (suggestions.brief || suggestions.hotel || suggestions.restaurants) {
          plan.suggestions = suggestions;
        }
        // Refeições: liga cada pausa a um restaurante já validado (sem novas
        // chamadas). Sem sugestão validada, a pausa fica genérica.
        if (suggestions.restaurants?.length) {
          const { attachMealVenues } = await import("./attach-meal-venues");
          plan.days = attachMealVenues(plan.days, suggestions.restaurants, negativePrefs);
        }
      } catch (e) {
        console.warn("pro suggestions failed", e);
      }

      // Contrato e reparação orientada: reutiliza o plano/candidatos existentes,
      // procura apenas o que falta e nunca cria uma segunda reserva de quota.
      const { extractTripRequirements } = await import("@/lib/trip-requirements");
      const { CURATED_ROUTE_TEMPLATES } = await import("@/lib/catalog/curated-route-templates");
      const { repairPlanContract } = await import("./contract-repair.server");
      plan.days = dropExcludedStops(plan.days, negativePrefs);
      const requirements = extractTripRequirements(data.prompt, {
        language: "pt",
        scopeText: data.cities.join(", ") || data.prompt,
      });
      const template = activeTemplate;
      void CURATED_ROUTE_TEMPLATES;
      const repaired = await repairPlanContract({
        plan,
        requirements,
        anchors: cityAnchors.map((anchor) => ({
          name: anchor.name,
          lat: anchor.lat,
          lng: anchor.lng,
        })),
        accommodationZoneCount: data.accommodationZoneCount,
        template,
        templateMode: data.templateMode,
        corridor: corridor ?? undefined,
        negativePreferences: negativePrefs,
      });
      // Validação final do corredor curado (geometria oficial obrigatória).
      const corridorIssues = corridor
        ? checkCorridorCompliance(
            corridor,
            repaired.plan.days.map((day) => {
              const anchor = cityAnchors.find(
                (item) => item.name.toLocaleLowerCase() === day.city.toLocaleLowerCase(),
              );
              return { city: day.city, lat: anchor?.lat, lng: anchor?.lng };
            }),
          ).map((issue) => ({ code: `corridor_${issue.code}`, message: issue.message }))
        : [];
      const { dayCoverageIssues } = await import("./day-coverage");
      // Reparação local antes de bloquear: um dia com paragens a menos recebe
      // uma segunda consulta de lugares na sua zona concreta (sem IA, sem novo
      // crédito) e só depois é revalidado.
      if (dayCoverageIssues(repaired.plan.days as never).length > 0) {
        const { repairIncompleteDays, filterRepairCandidates } = await import("./day-repair");
        const { fetchCandidates, fetchZoneRepairCandidates } = await import("./candidates.server");
        repaired.plan.days = (await repairIncompleteDays(repaired.plan.days as never, {
          anchors: cityAnchors.map((anchor) => ({
            name: anchor.name,
            lat: anchor.lat,
            lng: anchor.lng,
          })),
          search: async ({ city, lat, lng, needed, excludeIds, excludeNames }) => {
            // 1) Proximidade real à zona do dia (queries diferentes das da
            // geração, senão a cache devolveria os mesmos POIs já usados).
            const nearby =
              lat !== undefined && lng !== undefined
                ? await fetchZoneRepairCandidates(lat, lng, negativePrefs)
                : [];
            let pool = filterRepairCandidates(nearby, {
              excludeIds,
              excludeNames,
              zone: { lat, lng },
              maxDistanceMeters: 45000,
            });
            // 2) Só se ainda faltarem paragens, alargar por texto na base.
            if (pool.length < needed) {
              const found = await fetchCandidates(
                { name: city, lat: lat ?? 0, lng: lng ?? 0 },
                interests,
                Math.max(12, needed * 6),
                negativePrefs,
                undefined,
                { days: 2 },
              );
              pool = [
                ...pool,
                ...filterRepairCandidates(found, {
                  excludeIds: new Set([...excludeIds, ...pool.map((p) => p.id)]),
                  excludeNames,
                  zone: { lat, lng },
                  maxDistanceMeters: 60000,
                }),
              ];
            }
            return pool;
          },
        })) as typeof repaired.plan.days;
      }

      const coverageIssues = dayCoverageIssues(repaired.plan.days as never).map((issue) => ({
        code: issue.code,
        message: issue.message,
      }));

      const finalViolations = [...repaired.violations, ...corridorIssues, ...coverageIssues];
      // Um dia vazio/fino nunca é um resultado Pro apresentável. buildDays já
      // tentou preencher com POIs reais da mesma base; se ainda falhou,
      // interromper antes de guardar e libertar a reserva no catch.
      if (coverageIssues.length > 0) {
        throw new Error(
          `Não foi possível preencher todos os dias com locais reais. ${coverageIssues.map((issue) => issue.message).join(" ")} Não contou para o teu limite.`,
        );
      }
      // Locais obrigatórios não resolvidos: o plano fica em rascunho com o
      // aviso explícito, em vez de fingir que foram incluídos.
      for (const missing of missingRequiredStops) {
        finalViolations.push({
          code: "required_stop_unresolved",
          message: `Não consegui confirmar "${missing}". Escolhe o local certo para o incluir no roteiro.`,
        });
      }
      plan = {
        ...repaired.plan,
        status: finalViolations.length ? "draft" : "ready",
        compliance: {
          valid: finalViolations.length === 0,
          repaired: true,
          violations: finalViolations,
          repairCandidates: repaired.repairCandidates,
          repairQuestion: repaired.question,
        },
      };

      // Contrato por cumprir: em vez de bloquear, devolvemos opções clicáveis
      // que reconstroem cidades → transferências → horários antes das
      // atrações. A nova tentativa não cria outra reserva de quota.
      const compliance = plan.compliance;
      if (compliance && !compliance.valid) {
        const { buildRepairStrategies } = await import("./repair-strategies");
        const strategies = buildRepairStrategies({
          requirements,
          countryCode: requirements.scope.countryCode ?? template?.countryCodes[0],
          days: totalDays,
          transport: transport === "car" || transport === "transit" ? transport : "transit",
          namedTemplate: template ?? null,
          language: "pt",
        });
        if (strategies.kind === "options") {
          compliance.repairStrategies = {
            message: strategies.message,
            hint: strategies.hint,
            options: strategies.options.map((option) => ({
              id: option.id,
              label: option.label,
              description: option.description,
              patch: { ...option.patch },
            })),
          };
        } else {
          compliance.repairAsk = { message: strategies.message, hint: strategies.hint };
        }
      }

      if (!isUsableProPlan(plan) && !plan.compliance?.repairStrategies) {
        // Antes de qualquer erro: transformar a pergunta de viabilidade em
        // opções clicáveis com patch real (menos cidades ou mais dias).
        const { buildFeasibilityStrategies } = await import("./feasibility-options");
        const feasibility = buildFeasibilityStrategies({
          cities: cityAnchors.length ? cityAnchors.map((anchor) => anchor.name) : (data.cities ?? []),
          days: totalDays,
          transport,
          themes: data.interests ?? [],
          templateId: template?.id,
          templateName: template?.name,
          templateMode: data.templateMode,
          accommodationZoneMin: data.accommodationZoneCount,
          language: "pt",
        });
        if (feasibility && plan.compliance) {
          plan.compliance.repairStrategies = feasibility;
        }
      }

      if (!isUsableProPlan(plan) && !plan.compliance?.repairStrategies && repaired.question) {
        // A reparação cronológica já tem uma pergunta estruturada. Nunca a
        // converter num erro/toast: devolvê-la como botões com patches reais,
        // mantendo exatamente a duração que o utilizador indicou.
        const { buildChronologyStrategies } = await import("./feasibility-options");
        const chronology = buildChronologyStrategies({
          cities: cityAnchors.length ? cityAnchors.map((anchor) => anchor.name) : (data.cities ?? []),
          days: totalDays,
          transport,
          question: repaired.question,
          themes: data.interests ?? [],
          templateId: template?.id,
          templateName: template?.name,
          templateMode: data.templateMode,
          accommodationZoneMin: data.accommodationZoneCount,
        });
        if (chronology && plan.compliance) {
          plan.compliance.repairStrategies = chronology;
        }
      }

      if (!isUsableProPlan(plan) && !plan.compliance?.repairStrategies) {
        // Sem opções seguras: pergunta concreta em vez de erro genérico.
        const question = repaired.question;
        throw new Error(
          plan.compliance?.repairAsk
            ? `${plan.compliance.repairAsk.message}\n\n${plan.compliance.repairAsk.hint}`
            : question
              ? `${question.message}\n\n1) ${question.options[0].label} — ${question.options[0].description}\n2) ${question.options[1].label} — ${question.options[1].description}\n\n${question.hint}`
              : "Não foi possível criar um roteiro utilizável. Não contou para o teu limite.",
        );
      }


      const { data: row, error } = await context.supabase
        .from("pro_itineraries")
        .insert({
          user_id: context.userId,
          title: plan.title,
          input: { ...data, intent, anchors: cityAnchors },
          plan,
          status: plan.status,
        })
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      if (!plan.compliance?.valid) {
        if (reservation) await releaseProUsageReservation(reservation);
      }
      await finishGenerationAttemptInternal(generationAttempt, "success", {
        model: "google/gemini-2.5-flash",
      });
      return { ...plan, id: row.id };
    } catch (error) {
      if (reservation) await releaseProUsageReservation(reservation);
      await finishGenerationAttemptInternal(generationAttempt, "technical_failure", {
        failureCode: "pro_generation_failed",
        model: "google/gemini-2.5-flash",
      });
      throw error;
    }
  });

export const listMyProItineraries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("pro_itineraries")
      .select("id,title,status,created_at,saved")
      .eq("user_id", context.userId)
      .eq("saved", true)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

function makeSlug(): string {
  return crypto.randomUUID().replaceAll("-", "").slice(0, 16);
}

export const saveProItinerary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid(), saved: z.boolean() }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: owned } = await context.supabase
      .from("pro_itineraries")
      .select("plan")
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .maybeSingle();
    const ownedPlan = owned?.plan as ProPlan | undefined;
    if (data.saved && ownedPlan?.compliance && !ownedPlan.compliance.valid) {
      throw new Error("Este roteiro ainda não cumpre o pedido e não pode ser guardado.");
    }
    const patch: { saved: boolean; share_slug?: string } = { saved: data.saved };
    if (data.saved) {
      const { data: existing } = await context.supabase
        .from("pro_itineraries")
        .select("share_slug")
        .eq("id", data.id)
        .eq("user_id", context.userId)
        .maybeSingle();
      if (!existing?.share_slug) patch.share_slug = makeSlug();
    }
    const { error } = await context.supabase
      .from("pro_itineraries")
      .update(patch)
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getProItinerary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("pro_itineraries")
      .select("id,title,plan,input,status,created_at,saved,share_slug")
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Not found");
    return row;
  });

export const getSharedProItinerary = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(4).max(40) }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: rows, error } = await sb.rpc("get_shared_pro_itinerary", { p_slug: data.slug });
    if (error) throw new Error(error.message);
    const row = Array.isArray(rows) ? rows[0] : rows;
    if (!row) throw new Error("Not found");
    return row as { id: string; title: string; plan: ProPlan; created_at: string };
  });

export const checkProAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => ({ hasPro: await hasProAccess(context.userId) }));

/** Regenerate only ONE day of a plan (keeps everything else). Cheap on IA. */
export const regenerateProDay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), day: z.number().int().min(1) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await ensurePro(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("pro_itineraries")
      .select("plan,input")
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error || !row) throw new Error(error?.message ?? "Not found");
    const plan = row.plan as ProPlan;
    const input = row.input as {
      anchors?: Array<{ name: string; lat: number; lng: number; days: number }>;
      intent?: { mood?: string; interests?: string[] };
      multiDestinationPlan?: MultiDestinationPlan;
    };
    const target = plan.days.find((d) => d.day === data.day);
    if (!target) throw new Error("Day not found");
    const anchor = input.anchors?.find((a) => a.name === target.city) ?? {
      name: target.city,
      lat: 0,
      lng: 0,
      days: 1,
    };
    const fresh = await buildDays(
      [{ ...anchor, days: 1 }],
      input.intent?.interests ?? [],
      plan.transport,
      plan.pace,
      input.intent?.mood ?? plan.mood,
    );
    if (fresh[0]) {
      fresh[0].day = data.day;
      // Regenerar nunca pode mudar a base definida no contrato vinculativo.
      fresh[0].city = target.city;
      plan.days = plan.days.map((d) => (d.day === data.day ? fresh[0] : d));
    }
    const expectedTotal = input.multiDestinationPlan
      ? input.multiDestinationPlan.stops.reduce((sum, stop) => sum + stop.days, 0)
      : plan.days.length;
    if (plan.days.length !== expectedTotal) {
      throw new Error("A regeneração alteraria a duração vinculativa do roteiro.");
    }
    const { dayCoverageIssues } = await import("./day-coverage");
    if (dayCoverageIssues(plan.days as never).length > 0) {
      throw new Error("Não foi possível regenerar este dia com locais reais; o roteiro anterior foi mantido.");
    }
    const { error: updErr } = await context.supabase
      .from("pro_itineraries")
      .update({ plan })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (updErr) throw new Error(updErr.message);
    return plan;
  });

// ─────────────────────────────────────────────────────────────────────────
// Manual editing: user can remove/reorder/add stops and swap meals.
// Client sends the mutated plan; server validates ownership + recomputes
// travelFromPrev per day. Cheap (no AI, no Places calls).
// ─────────────────────────────────────────────────────────────────────────

const ProStopSchema = z.object({
  kind: z.enum(["poi", "meal", "break", "parking", "transfer", "experience"]),
  category: z.string().max(80).optional(),
  name: z.string().min(1).max(240),
  time: z.string().max(10).default(""),
  durationMin: z.number().int().min(0).max(600).default(0),
  lat: z.number().optional(),
  lng: z.number().optional(),
  placeId: z.string().max(200).optional(),
  note: z.string().max(1000).optional(),
  officialUrl: z.string().url().max(500).optional(),
  templateId: z.string().max(120).optional(),
  venue: z
    .object({
      name: z.string().max(240),
      lat: z.number(),
      lng: z.number(),
      placeId: z.string().max(200).optional(),
      address: z.string().max(300).optional(),
      rating: z.number().nullable().optional(),
    })
    .optional(),
  travelFromPrev: z
    .object({
      minutes: z.number(),
      meters: z.number(),
      label: z.string(),
    })
    .optional(),
});

const UpdatePlanInput = z.object({
  id: z.string().uuid(),
  days: z
    .array(
      z.object({
        day: z.number().int().min(1),
        city: z.string().max(120),
        intro: z.string().max(2000).optional(),
        stops: z.array(ProStopSchema).max(40),
      }),
    )
    .max(30),
});

function recomputeDayTravel(stops: ProDayStop[], mode: Mode): ProDayStop[] {
  let prev: { lat: number; lng: number } | null = null;
  return stops.map((s) => {
    const next = { ...s };
    if (s.lat != null && s.lng != null) {
      if (prev) {
        const m = haversineMeters(prev, { lat: s.lat, lng: s.lng });
        next.travelFromPrev = {
          meters: Math.round(m),
          minutes: travelMinutes(m, mode),
          label: modeLabel(mode),
        };
      } else {
        delete next.travelFromPrev;
      }
      prev = { lat: s.lat, lng: s.lng };
    } else {
      delete next.travelFromPrev;
    }
    return next;
  });
}

export const updateProPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => UpdatePlanInput.parse(d))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("pro_itineraries")
      .select("plan,input,user_id")
      .eq("id", data.id)
      .maybeSingle();
    if (error || !row) throw new Error(error?.message ?? "Not found");
    if (row.user_id !== context.userId) throw new Error("Forbidden");
    const plan = row.plan as ProPlan;
    const nextDays: ProDay[] = data.days.map((d) => ({
      day: d.day,
      city: d.city,
      intro: d.intro,
      stops: recomputeDayTravel(d.stops as ProDayStop[], plan.transport),
    }));
    let nextPlan: ProPlan = { ...plan, days: nextDays };
    const storedInput = row.input as z.infer<typeof GenerateInput> & {
      anchors?: Array<{ name: string; lat: number; lng: number }>;
    };
    const { extractTripRequirements } = await import("@/lib/trip-requirements");
    const { CURATED_ROUTE_TEMPLATES } = await import("@/lib/catalog/curated-route-templates");
    const { repairPlanContract } = await import("./contract-repair.server");
    const requirements = extractTripRequirements(storedInput.prompt ?? "", {
      language: "pt",
      scopeText: storedInput.cities?.join(", ") || storedInput.prompt,
    });
    const template = storedInput.templateId
      ? CURATED_ROUTE_TEMPLATES.find(
          (candidate) => candidate.id === storedInput.templateId && candidate.active,
        )
      : undefined;
    const checked = await repairPlanContract({
      plan: nextPlan,
      requirements,
      anchors: storedInput.anchors ?? [],
      accommodationZoneCount: storedInput.accommodationZoneCount ?? 0,
      template,
    });
    nextPlan = {
      ...checked.plan,
      status: checked.violations.length ? "draft" : "ready",
      compliance: {
        valid: checked.violations.length === 0,
        repaired: true,
        violations: checked.violations,
        repairCandidates: checked.repairCandidates,
      },
    };
    const { error: updErr } = await context.supabase
      .from("pro_itineraries")
      .update({ plan: nextPlan })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (updErr) throw new Error(updErr.message);
    return { ...nextPlan, id: data.id };
  });

// Search Places for adding a new stop or swapping a meal.
const SearchInput = z.object({
  query: z.string().min(1).max(120),
  near: z.object({ lat: z.number(), lng: z.number() }).optional(),
  mealsOnly: z.boolean().default(false),
});
export const searchProPlace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SearchInput.parse(d))
  .handler(async ({ data }) => {
    const key = process.env.GOOGLE_MAPS_SERVER_API_KEY;
    if (!key) return { results: [] };
    const body: Record<string, unknown> = {
      textQuery: data.mealsOnly ? `${data.query} restaurant` : data.query,
      maxResultCount: 8,
    };
    if (data.near)
      body.locationBias = {
        circle: { center: { latitude: data.near.lat, longitude: data.near.lng }, radius: 8000 },
      };
    if (data.mealsOnly) body.includedType = "restaurant";
    try {
      const r = await cachedPlacesFetch("https://places.googleapis.com/v1/places:searchText", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask":
            "places.id,places.displayName,places.location,places.formattedAddress,places.rating",
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(20_000),
      }, { kind: data.mealsOnly ? "pro_dining" : "pro_places", ttlMs: data.mealsOnly ? PLACES_TTL.dining : PLACES_TTL.attractions });
      if (!r.ok) return { results: [] };
      const j = (await r.json()) as {
        places?: Array<{
          id?: string;
          displayName?: { text?: string };
          location?: { latitude?: number; longitude?: number };
          formattedAddress?: string;
          rating?: number;
        }>;
      };
      const results = (j.places ?? []).flatMap((p) => {
        if (!p.id || !p.displayName?.text || !p.location?.latitude || !p.location?.longitude)
          return [];
        return [
          {
            id: p.id,
            name: p.displayName.text,
            lat: p.location.latitude,
            lng: p.location.longitude,
            address: p.formattedAddress ?? "",
            rating: p.rating ?? null,
          },
        ];
      });
      return { results };
    } catch {
      return { results: [] };
    }
  });
